// Strips secrets from URLs before PostHog events leave the browser.
//
// PostHog records the page URL, referrer and clicked link hrefs on every event.
// Ours can carry credentials: shared projects pass their access token as
// ?token=, Bobbie and Control Center pass theirs in the URL hash, and the
// Zitadel callback carries ?code=&state=. UTM parameters are already extracted
// into their own properties by PostHog, so dropping every query string and
// hash loses nothing we analyse.

// Route segments that are secrets themselves. The viewer has none today (its
// tokens travel in the query or hash); add a pattern here if a route ever
// carries one in its path.
const SECRET_PATH_SEGMENTS = [];

// Property keys whose values are URLs or paths ($current_url, $referrer,
// $pathname, $initial_current_url, attr__href, ...).
const URL_KEY = /url|referrer|pathname|href/i;

export function sanitizeUrl(value) {
  if (typeof value !== "string") return value;
  let url = value.replace(/[?#].*$/, "");
  for (const [pattern, replacement] of SECRET_PATH_SEGMENTS) {
    url = url.replace(pattern, replacement);
  }
  return url;
}

// Autocapture keeps only these element attributes (allowlist): id and
// data-ph identify what was clicked, href is a sanitized URL, class the CSS
// classes. Anything else — title, aria-label, custom data-* — can carry
// customer data, so it is dropped even if no ignore list names it.
const ALLOWED_ATTRIBUTES = ["id", "data-ph", "href", "class"];

function isDroppedAttribute(key) {
  return key.startsWith("attr__") && !ALLOWED_ATTRIBUTES.includes(key.slice(6));
}

// $elements_chain is not parsed (its escaping is ambiguous) but rebuilt from
// the already sanitized $elements, the way posthog-js serialises it
// (elementsToString in @posthog/browser-common).
const escapeQuotes = (value) => value.replace(/"|\\"/g, '\\"');

function classList(element) {
  const classes = element.attr__class;
  if (Array.isArray(classes)) return classes;
  return typeof classes === "string" ? classes.split(" ").filter(Boolean) : [];
}

function elementsToChain(elements) {
  return elements
    .map((element) => {
      let chain = element.tag_name || "";
      [...classList(element)].sort().forEach((name) => {
        chain += `.${name.replace(/"/g, "")}`;
      });
      const attributes = {
        ...(element.$el_text ? { text: element.$el_text.slice(0, 400) } : {}),
        "nth-child": element.nth_child ?? 0,
        "nth-of-type": element.nth_of_type ?? 0,
        ...(element.attr__href
          ? { href: element.attr__href.slice(0, 2048) }
          : {}),
        ...(element.attr__id ? { attr_id: element.attr__id } : {}),
        ...Object.fromEntries(
          Object.entries(element).filter(([key]) => key.startsWith("attr__")),
        ),
      };
      chain += ":";
      chain += Object.entries(attributes)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(
          ([key, value]) =>
            `${escapeQuotes(key)}="${escapeQuotes(String(value))}"`,
        )
        .join("");
      return chain;
    })
    .join(";");
}

// Drop the client's $elements_chain; rebuild it from $elements if present,
// otherwise send none (fail closed) — PostHog can work from $elements.
function rebuildElementsChain(properties) {
  if (!properties || !("$elements_chain" in properties)) return properties;
  const rest = { ...properties };
  delete rest.$elements_chain;
  if (!Array.isArray(rest.$elements)) return rest;
  return { ...rest, $elements_chain: elementsToChain(rest.$elements) };
}

// Absolute URLs embedded in free text, e.g. "Request to https://…?token=… failed"
// in $exception_message / $exception_list. Matches stop at ")" so text like
// "(/x?token=…)" keeps its closing parenthesis.
const EMBEDDED_URL = /https?:\/\/[^\s"'<>)]+/g;

// Root-relative paths with a query or hash, e.g. "GET /x/?token=… 404".
const EMBEDDED_RELATIVE_QUERY = /(^|[\s"'(=])(\/[^\s"'<>?#)]*)[?#][^\s"'<>)]*/g;

// Safety net for URLs the patterns above miss (e.g. "url:/x?token=…"): the
// value of any credential-like query parameter, wherever it appears in text.
const SECRET_PARAM =
  /((?:^|[?&#;\s,])(?:[a-z_]*token|code|state|session_state|key|secret|signature|password)=)[^&\s"'<>,)]+/gi;

// JWTs in any other form, e.g. a serialised header {"X-Share-Token":"eyJ…"}:
// three base64url segments, the first one starting with eyJ ('{"').
const JWT = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;

export function sanitizeEmbeddedUrls(text) {
  let result = text
    .replace(JWT, "[redacted-jwt]")
    .replace(SECRET_PARAM, "$1[redacted]")
    .replace(EMBEDDED_URL, (url) => sanitizeUrl(url))
    .replace(EMBEDDED_RELATIVE_QUERY, "$1$2");
  for (const [pattern, replacement] of SECRET_PATH_SEGMENTS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

function sanitizeValue(key, value) {
  if (Array.isArray(value)) return value.map((item) => sanitizeValue("", item));
  if (value && typeof value === "object") return sanitizeObject(value);
  if (typeof value !== "string") return value;
  if (URL_KEY.test(key)) return sanitizeUrl(value);
  return sanitizeEmbeddedUrls(value);
}

function sanitizeObject(object) {
  const result = {};
  for (const [key, value] of Object.entries(object)) {
    if (isDroppedAttribute(key)) continue;
    result[key] = sanitizeValue(key, value);
  }
  return result;
}

// posthog-js `before_send` hook: covers properties, $set and $set_once.
export function sanitizePostHogEvent(event) {
  if (!event) return event;
  return {
    ...event,
    properties:
      event.properties &&
      rebuildElementsChain(sanitizeObject(event.properties)),
    $set: event.$set && sanitizeObject(event.$set),
    $set_once: event.$set_once && sanitizeObject(event.$set_once),
  };
}
